import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const seguros = [
  {
    icon: "ri-heart-pulse-line",
    title: "Seguro Saúde Empresarial",
    desc: "Proteção e acesso à saúde, retenção de talentos e redução de afastamentos.",
    tag: "Mais contratado",
    highlight: true,
  },
  {
    icon: "ri-shield-user-line",
    title: "Seguro de Vida em Grupo",
    desc: "Segurança financeira para colaboradores e suas famílias em momentos críticos.",
    tag: null,
    highlight: false,
  },
  {
    icon: "ri-tooth-line",
    title: "Seguro Odontológico",
    desc: "Foco em prevenção e bem-estar bucal com ampla rede credenciada.",
    tag: null,
    highlight: false,
  },
  {
    icon: "ri-building-2-line",
    title: "Seguro Empresarial",
    desc: "Proteção do patrimônio contra incêndios, roubos e danos estruturais.",
    tag: null,
    highlight: false,
  },
  {
    icon: "ri-scales-3-line",
    title: "Seguro de Responsabilidade Civil",
    desc: "Cobertura completa para danos causados a terceiros em suas operações.",
    tag: "RC",
    highlight: false,
  },
  {
    icon: "ri-briefcase-4-line",
    title: "Seguro D&O",
    desc: "Proteção para diretores e administradores contra riscos de gestão.",
    tag: null,
    highlight: false,
  },
  {
    icon: "ri-car-line",
    title: "Seguro Frota",
    desc: "Proteção logística e comercial para toda a frota de veículos da empresa.",
    tag: null,
    highlight: false,
  },
  {
    icon: "ri-file-shield-2-line",
    title: "Seguro Garantia",
    desc: "Cobertura para obrigações contratuais, licitações e concursos públicos.",
    tag: null,
    highlight: false,
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "Seguro Cyber",
    desc: "Proteção contra ataques virtuais, vazamentos de dados e crimes cibernéticos.",
    tag: "Essencial",
    highlight: false,
  },
  {
    icon: "ri-truck-line",
    title: "Seguro de Transporte / Carga",
    desc: "Cobertura completa de mercadorias em trânsito nacional e internacional.",
    tag: null,
    highlight: false,
  },
];

export default function HubSeguros() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".seg-card").forEach((card, i) => {
              setTimeout(() => card.classList.add("seg-visible"), i * 60);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="hub-seguros" className="py-16 lg:py-20 bg-[#F8F8F6]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-black" />
              <span className="text-black text-xs font-semibold tracking-widest uppercase">
                Hub de Seguros
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight">
              As bases da proteção corporativa:
            </h2>
          </div>
          <div className="max-w-sm">
            <p className="text-black/70 text-base leading-relaxed mb-4 lg:text-right">
              Cobertura completa para proteger sua empresa, colaboradores e patrimônio em todas as frentes.
            </p>
            <div className="flex lg:justify-end">
              <Link
                to="/servicos/hub-de-seguros"
                className="whitespace-nowrap text-black text-xs font-semibold hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1"
              >
                Saiba mais <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {seguros.map((seg) => (
            <div
              key={seg.title}
              className={`seg-card opacity-0 translate-y-6 transition-all duration-500 relative rounded-2xl p-5 border cursor-default group bg-white hover:shadow-lg hover:-translate-y-1 ${
                seg.highlight
                  ? "border-[#FBC52D] ring-1 ring-[#FBC52D]/20 shadow-md shadow-[#FBC52D]/10"
                  : "border-gray-100 hover:border-[#FBC52D]/40"
              }`}
            >
              {/* Tag */}
              {seg.tag && (
                <div className="absolute top-4 right-4">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    seg.highlight ? "bg-[#FBC52D] text-black" : "bg-[#FBC52D]/15 text-[#FBC52D]"
                  }`}>
                    {seg.tag}
                  </span>
                </div>
              )}

              <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4 transition-colors bg-[#FBC52D]/10 group-hover:bg-[#FBC52D]/20">
                <i className={`${seg.icon} text-[#FBC52D] text-lg`} />
              </div>

              <h3 className="font-bold text-sm mb-2 leading-tight text-black">
                {seg.title}
              </h3>
              <p className="text-xs leading-relaxed text-black/70">
                {seg.desc}
              </p>
            </div>
          ))}

          {/* CTA Card */}
          <div className="seg-card opacity-0 translate-y-6 transition-all duration-500 bg-[#0D0D0D] rounded-2xl p-5 flex flex-col justify-between cursor-pointer group hover:bg-[#1a1a1a] transition-colors border border-[#0D0D0D]">
            <div>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#FBC52D]/15 mb-4">
                <i className="ri-add-circle-line text-[#FBC52D] text-xl" />
              </div>
              <h3 className="text-white font-bold text-sm mb-2 leading-tight">
                Precisa de algo específico?
              </h3>
              <p className="text-white/60 text-xs leading-relaxed">
                Soluções personalizadas para cada necessidade da sua empresa.
              </p>
            </div>
            <button
              onClick={() => document.querySelector("#contato")?.scrollIntoView({ behavior: "smooth" })}
              className="whitespace-nowrap mt-4 bg-[#FBC52D] text-black text-xs font-semibold px-4 py-2.5 rounded-full hover:bg-[#d4a800] transition-colors w-fit"
            >
              Consultar
            </button>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-12 bg-[#0D0D0D] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="w-4 h-px bg-[#FBC52D]" />
              <span className="text-[#FBC52D] text-xs font-semibold tracking-widest uppercase">Análise gratuita</span>
            </div>
            <h3 className="text-white font-bold text-2xl mb-2 leading-tight">Não sabe qual seguro é ideal para você?</h3>
            <p className="text-gray-400 text-sm max-w-lg">Nossa equipe realiza um diagnóstico completo e gratuito das necessidades da sua empresa. Sem compromisso.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => document.querySelector("#contato")?.scrollIntoView({ behavior: "smooth" })}
              className="whitespace-nowrap bg-[#FBC52D] text-black font-bold px-8 py-4 rounded-full hover:bg-[#d4a800] transition-colors text-sm cursor-pointer"
            >
              Solicitar diagnóstico gratuito
            </button>
            <a
              href="https://wa.me/5511936189998"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/8 transition-colors text-sm cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base" />
              Falar pelo WhatsApp
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .seg-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}